import React from "react";
import {
  DriveItem,
  DragState,
  EditState,
  FileActionsConfig,
  SelectionState,
} from "../types";
import FileActionsMenu from "./FileActionsMenu";
import FilePreview from "./FilePreview";

interface FileGridViewProps {
  /** "card": rich My Folder card with a 250px preview; "thumb": compact Shared/Official card. */
  variant: "card" | "thumb";
  /** Cards to render (already sorted/sliced by the caller). */
  rows: DriveItem[];
  scroll: boolean;
  onItemDoubleClick: (item: DriveItem) => void;
  actions: FileActionsConfig;
  edit?: EditState;
  /** Selection + drag enable the interactive My Folder behaviour (card variant). */
  selection?: SelectionState;
  drag?: DragState;
}

const gridContainerStyle = (scroll: boolean): React.CSSProperties => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
  gap: "16px",
  maxHeight: scroll ? "700px" : "none",
  overflowY: scroll ? "auto" : "visible",
  paddingRight: "8px",
  width: "100%",
});

const nameStyle: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 500,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

/** Card grid shared by the My Folder, Shared and Official views. */
const FileGridView = ({
  variant,
  rows,
  scroll,
  onItemDoubleClick,
  actions,
  edit,
  selection,
  drag,
}: FileGridViewProps) => {
  const editInputClass =
    variant === "card" ? "form-control form-control-sm" : "form-control";

  const renderName = (item: DriveItem) =>
    edit && edit.editingItemId === item.id ? (
      <input
        type="text"
        className={editInputClass}
        value={edit.editName}
        onChange={(e) => edit.setEditName(e.target.value)}
        onKeyDown={(e) => edit.onKeyDown(e, item)}
        onBlur={() => edit.onBlur(item)}
        autoFocus
      />
    ) : (
      <p className="mb-0" style={nameStyle} title={item.name}>
        {item.name}
      </p>
    );

  return (
    <div style={gridContainerStyle(scroll)}>
      {rows.map((item) => {
        if (variant === "card") {
          const isSelected =
            selection?.selectedItems.some((sel) => sel.id === item.id) ?? false;

          return (
            <div
              key={item.id}
              className="file-card"
              onDoubleClick={() => onItemDoubleClick(item)}
              onClick={(e) => selection?.onItemClick(item, e)}
              onContextMenu={(e) => selection?.onContextMenu(e, item)}
              draggable
              onDragStart={(e) => drag?.onDragStart(e, item)}
              onDragOver={(e) => drag?.onDragOver(e, item)}
              onDragLeave={drag?.onDragLeave}
              onDrop={(e) => drag?.onDrop(e, item)}
              style={{
                backgroundColor: isSelected ? "#e9ecef" : "transparent",
                border:
                  drag?.dragOverItem === item.id
                    ? "2px dashed #007bff"
                    : "1px solid #ddd",
                cursor: "grab",
                opacity: drag?.isDragging && isSelected ? 0.5 : 1,
              }}
            >
              <div className="file-grid-container">
                <div
                  className="file-card"
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: 6,
                    overflow: "hidden",
                    background: "#fff",
                    marginBottom: "16px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                  }}
                >
                  <div
                    className="file-header"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 10px",
                      borderBottom: "1px solid #eee",
                      background: "#f8f9fa",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>{renderName(item)}</div>
                    <FileActionsMenu item={item} layout="grid" actions={actions} />
                  </div>
                  <FilePreview
                    item={item}
                    variant="card"
                    onDoubleClick={() => onItemDoubleClick(item)}
                  />
                </div>
              </div>
            </div>
          );
        }

        // variant === "thumb"
        return (
          <div
            key={item.id}
            className="file-card"
            onDoubleClick={() => onItemDoubleClick(item)}
          >
            <div className="file-grid-container">
              <div
                className="file-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>{renderName(item)}</div>
                <FileActionsMenu item={item} layout="grid" actions={actions} />
              </div>
              <FilePreview
                item={item}
                variant="thumb"
                onDoubleClick={() => onItemDoubleClick(item)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FileGridView;
